/**
 * Streaming Suspense - Condition-based suspension for streaming data.
 *
 * Components suspend while streaming AND data is incomplete (based on conditions).
 * When data meets conditions OR streaming ends, suspension resolves.
 *
 * IMPORTANT: The parent component MUST re-render when the value changes
 * for Suspense to resolve. This happens automatically when:
 * - Parent state changes (e.g., slides prop updates during streaming)
 * - Parent receives new props from its parent
 *
 * It does NOT happen automatically when:
 * - The value object is mutated in place without state update
 *
 * @example
 * ```tsx
 * function OptionButton({ option, isStreaming }) {
 *   // Suspend until option has id and label
 *   useStreamingSuspense(option, isStreaming, ['id', 'label']);
 *   return <button>{option.label}</button>;
 * }
 *
 * // Parent wraps with Suspense
 * <Suspense fallback={<OptionSkeleton />}>
 *   <OptionButton option={option} isStreaming={isStreaming} />
 * </Suspense>
 * ```
 */
import { createContext, type ReactNode, use, useState } from 'react';

interface PromiseEntry {
  promise: Promise<void>;
  resolve: () => void;
  resolved: boolean;
}

type ConditionKey = string[] | ((value: unknown) => boolean) | undefined;

/**
 * Registry that maintains promises per object+condition combination.
 * Different conditions on the same object get independent promises.
 */
class PromiseRegistry {
  private promises = new Map<string, PromiseEntry>();
  private objectIds = new WeakMap<object, number>();
  private subscribers = new Map<string, Set<symbol>>();
  private nextId = 0;

  private getObjectId(obj: object): number {
    const existing = this.objectIds.get(obj);
    if (existing !== undefined) {
      return existing;
    }

    const id = this.nextId++;
    this.objectIds.set(obj, id);
    return id;
  }

  private serializeCondition(condition: ConditionKey): string {
    if (!condition) {
      return 'none';
    }
    if (typeof condition === 'function') {
      // Functions get unique IDs based on reference
      // Note: Same function reference = same key (intentional)
      return `fn:${condition.toString().slice(0, 100)}`;
    }
    return `keys:${[...condition].sort().join(',')}`;
  }

  private getCompositeKey(obj: object, condition: ConditionKey): string {
    return `${this.getObjectId(obj)}:${this.serializeCondition(condition)}`;
  }

  /**
   * Get or create a promise for an object+condition combination.
   * Returns the same promise for the same object+condition (stable identity).
   */
  getPromise(obj: object, condition: ConditionKey): PromiseEntry {
    const key = this.getCompositeKey(obj, condition);
    const existing = this.promises.get(key);
    if (existing) {
      return existing;
    }

    let resolveRef = () => {
      // Placeholder until promise executor assigns the real resolver
    };
    const promise = new Promise<void>((r) => {
      resolveRef = r;
    });
    const entry: PromiseEntry = { promise, resolve: resolveRef, resolved: false };
    this.promises.set(key, entry);
    return entry;
  }

  /**
   * Resolve the promise for an object+condition (idempotent).
   */
  resolve(obj: object, condition: ConditionKey): void {
    const key = this.getCompositeKey(obj, condition);
    const entry = this.promises.get(key);
    if (entry && !entry.resolved) {
      entry.resolved = true;
      entry.resolve();
    }
  }

  /**
   * Subscribe to a promise (call in useEffect).
   * Returns a subscriber ID for cleanup.
   */
  subscribe(obj: object, condition: ConditionKey): symbol {
    const key = this.getCompositeKey(obj, condition);
    const subs = this.subscribers.get(key) ?? new Set();
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, subs);
    }
    const subscriberId = Symbol('subscriber');
    subs.add(subscriberId);
    return subscriberId;
  }

  /**
   * Unsubscribe from a promise (call in cleanup).
   * Resolves promise if no subscribers remain.
   */
  unsubscribe(obj: object, condition: ConditionKey, subscriberId: symbol): void {
    const key = this.getCompositeKey(obj, condition);
    const subs = this.subscribers.get(key);
    if (!subs) {
      return;
    }

    subs.delete(subscriberId);

    // If no more subscribers, clean up the promise
    if (subs.size === 0) {
      this.resolve(obj, condition);
      this.promises.delete(key);
      this.subscribers.delete(key);
    }
  }

  /**
   * Check if an object+condition has a pending (unresolved) promise.
   */
  hasPending(obj: object, condition: ConditionKey): boolean {
    const key = this.getCompositeKey(obj, condition);
    const entry = this.promises.get(key);
    return entry !== undefined && !entry.resolved;
  }
}

// Global registry - promises are tied to object+condition identity
const globalRegistry = new PromiseRegistry();

/**
 * Context for scoped promise registries.
 * Allows different streaming sessions to be isolated.
 */
const PromiseRegistryContext = createContext<PromiseRegistry | null>(null);

export interface StreamingSuspenseProviderProps {
  children: ReactNode;
}

/**
 * Provider that creates a scoped promise registry for streaming suspense.
 * Use this to isolate streaming sessions (e.g., different chat threads).
 * If not provided, a global registry is used.
 */
export function StreamingSuspenseProvider({ children }: StreamingSuspenseProviderProps) {
  const [registry] = useState(() => new PromiseRegistry());

  return <PromiseRegistryContext value={registry}>{children}</PromiseRegistryContext>;
}

/**
 * Get the current registry (scoped or global fallback).
 */
function _useRegistry(): PromiseRegistry {
  const contextRegistry = use(PromiseRegistryContext);
  return contextRegistry ?? globalRegistry;
}

/**
 * Type guard for plain objects (not arrays, not null).
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Check if a value meets the required conditions.
 *
 * @param value - The value to check
 * @param condition - Required keys, predicate function, or undefined (always ready)
 * @param isStreaming - Whether streaming is active (affects empty string handling)
 *
 * @remarks
 * For key-based conditions, values are considered "incomplete" if:
 * - The key doesn't exist
 * - The value is `undefined` or `null`
 * - The value is an empty string `''` (only during streaming)
 *
 * Empty strings are treated as incomplete during streaming because partial tokens
 * often result in empty strings before the full value arrives. After streaming ends,
 * empty strings are accepted as valid final values.
 */
function _meetsCondition(value: unknown, condition: ConditionKey, isStreaming: boolean): boolean {
  if (!condition) {
    return true;
  }

  if (typeof condition === 'function') {
    return condition(value);
  }

  // Array of required keys - only works on plain objects
  if (!isPlainObject(value)) {
    return false;
  }

  return condition.every((key) => {
    const val = value[key];

    // Always reject undefined/null
    if (val === undefined || val === null) {
      return false;
    }

    // During streaming: empty strings are incomplete (tokens still arriving)
    // After streaming: empty strings are valid final values
    if (isStreaming && val === '') {
      return false;
    }

    return true;
  });
}

/**
 * Suspend the component while streaming if conditions are not met.
 *
 * @param value - The streaming value to check
 * @param isStreaming - Whether streaming is active
 * @param condition - When to consider data "ready":
 *   - string[]: Required keys that must have non-empty values
 *   - (value) => boolean: Custom predicate
 *   - undefined: Always ready (never suspends)
 *
 * @example
 * ```tsx
 * // Suspend until id and label exist
 * useStreamingSuspense(option, isStreaming, ['id', 'label']);
 *
 * // Suspend until custom condition
 * useStreamingSuspense(option, isStreaming, (opt) => opt.label?.length > 3);
 *
 * // Never suspend (just mark as streaming-aware)
 * useStreamingSuspense(option, isStreaming);
 * ```
 */
// biome-ignore lint/style/useComponentExportOnlyModules: Hook is the primary export of this module alongside the Provider component
export function useStreamingSuspense(
  _value: unknown,
  _isStreaming: boolean,
  _condition?: string[] | ((value: unknown) => boolean),
): void {
  // const registry = useRegistry();
  //
  // // Cleanup on unmount
  // useEffect(() => {
  //   if (!isPlainObject(value) || !isStreaming) {
  //     return;
  //   }
  //
  //   const subscriberId = registry.subscribe(value, condition);
  //   return () => registry.unsubscribe(value, condition, subscriberId);
  // }, [registry, value, condition, isStreaming]);
  //
  // // Non-objects can't have promises attached
  // if (!isPlainObject(value)) {
  //   return;
  // }
  //
  // const isReady = meetsCondition(value, condition, isStreaming);
  //
  // if (isReady || !isStreaming) {
  //   // Condition met or streaming ended - resolve any pending promise
  //   registry.resolve(value, condition);
  //   return;
  // }
  //
  // // Still streaming and condition not met - suspend
  // const { promise } = registry.getPromise(value, condition);
  // throw promise;
}
